// ============================================================
// HowItWorksModal.js — InvestorQ Top Winners explainer
// Animated data flow pipeline showing how the agent works
// Import into TopWinnersAgent.js and drop <HowItWorksModal />
// before the disclaimer at the bottom of the return.
// Trigger: add this button anywhere in the header:
//   <HowItWorksButton onClick={() => setShowHowItWorks(true)} />
// ============================================================
import React, { useState, useEffect, useRef } from 'react';

// ── Trigger button — drop this next to the Run/Refresh buttons ──
export const HowItWorksButton = ({ onClick }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: '0.45rem',
      padding: '0.6rem 1.1rem', borderRadius: '10px', cursor: 'pointer',
      background: 'rgba(255,255,255,0.04)', border: '1px solid #1e293b',
      color: '#64748b', fontWeight: 600, fontSize: '0.82rem',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      transition: 'all 0.2s',
    }}
    onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.borderColor = '#334155'; }}
    onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#1e293b'; }}
  >
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="7" cy="7" r="6" stroke="#64748b" strokeWidth="1.2"/>
      <text x="7" y="11" textAnchor="middle" fill="#64748b" fontSize="9" fontWeight="700">?</text>
    </svg>
    How it works
  </button>
);

// ── Pipeline stages data ──────────────────────────────────────
const STAGES = [
  {
    id: 'pool',
    label: 'Build pool',
    color: '#3b82f6',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="4" width="7" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/>
        <rect x="11" y="4" width="7" height="12" rx="2" stroke="currentColor" strokeWidth="1.3" strokeDasharray="2 1.5"/>
        <path d="M9 10h2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
    title: '50 anchor + 50 dynamic stocks',
    detail: 'Every run starts by building a candidate pool of ~100 stocks. The anchor core is 50 stable large-caps spanning all 11 GICS sectors — Technology, Healthcare, Financials, Energy, and more. These are tracked every run so the history layer can detect patterns. The dynamic half is discovered live from today\'s market: top volume gainers, most actively traded, and recent IPOs pulled fresh from Alpha Vantage\'s live listing data. This means the agent can find breakout stocks that weren\'t on any watchlist yesterday.',
    stats: [
      { label: 'Anchor stocks', value: '50', sub: 'All 11 GICS sectors' },
      { label: 'Dynamic picks', value: '~50', sub: 'Live market movers' },
      { label: 'Total scanned', value: '~100', sub: 'Per run' },
    ],
  },
  {
    id: 'signals',
    label: 'Fetch 6 signals',
    color: '#8b5cf6',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 2v3M10 15v3M2 10h3M15 10h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.3"/>
        <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
      </svg>
    ),
    title: '6 independent signal layers per stock',
    detail: 'For every stock in the pool, six independent data streams are fetched in parallel via Alpha Vantage. Fundamentals (PE ratio, ROE, profit margins, analyst targets). Earnings quality (beat rate over 8 quarters, EPS growth year-on-year, average surprise %). Alpha Intelligence news sentiment (30-day scan: bullish %, sentiment shift detection, high-impact article scoring, M&A and insider flags). RSI momentum vs 50-day and 200-day moving averages. Real current price from live quote. Reddit/social sentiment via Tradestie.',
    stats: [
      { label: 'Earnings lookback', value: '8 qtrs', sub: 'Beat rate + surprise' },
      { label: 'News articles', value: '50', sub: 'Per stock, 30 days' },
      { label: 'API calls saved', value: '4hr cache', sub: 'No re-fetching' },
    ],
  },
  {
    id: 'history',
    label: 'Load history',
    color: '#06b6d4',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 10a7 7 0 1 0 14 0A7 7 0 0 0 3 10z" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M10 6v4l3 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 3l2 2M15 3l-2 2" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.5"/>
      </svg>
    ),
    title: 'Every run stored — patterns compound over time',
    detail: 'Before Claude ranks anything, the agent queries three Postgres tables that store every prior run. It computes which stocks have appeared consistently (ranked top-10 in 60%+ of runs), which are rising in rank, which just dropped out after strong showings, and what the signal trends look like for each stock across runs. This historical intelligence is sent to Claude alongside the current matrix. The more runs completed, the richer the patterns — stocks that consistently rank high earn a conviction boost, while re-entrants after dropping out get extra scrutiny.',
    stats: [
      { label: 'DB tables', value: '3', sub: 'Runs · picks · signals' },
      { label: 'History depth', value: '10 runs', sub: 'Context window' },
      { label: 'Threshold', value: '60%', sub: 'Consistency badge' },
    ],
  },
  {
    id: 'claude',
    label: 'Claude synthesis',
    color: '#a78bfa',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M4 14l3-7 3 5 2-3 4 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="15" cy="5" r="2" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
    ),
    title: 'History-aware AI ranking with conviction scoring',
    detail: 'Claude Sonnet receives the slim signal matrix (25 key fields per stock, stripped of noise) plus the structured historical intelligence block. It applies a weighted ranking: earnings quality first, then sentiment, fundamentals, momentum, social signals, and finally historical consistency. Each stock gets a conviction score (60-99), a trend label, a "why now" rationale citing specific numbers, a historical note, and a risk flag. Sector diversity is enforced — max 3 picks from any one sector. Dynamic discoveries are explicitly labeled so users know what the agent found vs what was in the anchor core.',
    stats: [
      { label: 'Model', value: 'Sonnet', sub: 'Claude Sonnet 4' },
      { label: 'Max tokens', value: '8,000', sub: 'Response budget' },
      { label: 'Ranking criteria', value: '8+', sub: 'Weighted signals' },
    ],
  },
  {
    id: 'output',
    label: 'Results + persist',
    color: '#f59e0b',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M10 3l1.8 5.4H17l-4.6 3.3 1.8 5.4L10 14l-4.2 3.1 1.8-5.4L3 8.4h5.2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
      </svg>
    ),
    title: 'Top 10 + sectors + discoveries, cached + stored',
    detail: 'Results are returned in three views: Top 10 Overall (highest conviction across all sectors), Sector Leaders (best pick per GICS sector), and Discoveries (dynamic stocks that made the cut — things the market is moving on that weren\'t pre-selected). The entire result is cached for 4 hours so subsequent users get instant results. Simultaneously, the run is persisted to Postgres — all 10 picks with scores, historical notes, and risk flags, plus the full 68-stock signal matrix snapshot. This data feeds the next run\'s history context, completing the intelligence loop.',
    stats: [
      { label: 'Cache TTL', value: '4 hrs', sub: 'In-memory' },
      { label: 'Signals stored', value: '68', sub: 'Full matrix per run' },
      { label: 'Next run', value: 'Smarter', sub: 'History compounds' },
    ],
  },
];

// ── Animated data packet ──────────────────────────────────────
const DataPacket = ({ color, delay, duration, style }) => {
  const [pos, setPos] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => {
      const interval = setInterval(() => {
        setPos(p => {
          if (p >= 100) { clearInterval(interval); return 0; }
          return p + 2;
        });
      }, duration / 50);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay, duration]);

  return (
    <div style={{
      position: 'absolute',
      left: `${pos}%`,
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: 8, height: 8,
      borderRadius: '50%',
      background: color,
      opacity: pos > 0 && pos < 100 ? 0.9 : 0,
      transition: 'opacity 0.2s',
      boxShadow: `0 0 6px ${color}`,
      ...style,
    }} />
  );
};

// ── Flow connector with animated packets ─────────────────────
const FlowLine = ({ color, active }) => {
  const packets = [0, 800, 1600];
  return (
    <div style={{
      flex: 1, height: 2, position: 'relative',
      background: active ? `${color}40` : 'rgba(255,255,255,0.06)',
      transition: 'background 0.4s',
      margin: '0 4px',
      alignSelf: 'center',
    }}>
      {active && packets.map((delay, i) => (
        <DataPacket key={i} color={color} delay={delay} duration={2400} />
      ))}
    </div>
  );
};

// ── Stage node ────────────────────────────────────────────────
const StageNode = ({ stage, isActive, isDone, onClick }) => (
  <div
    onClick={onClick}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: '0.4rem', cursor: 'pointer', minWidth: 64, maxWidth: 80,
      padding: '0.5rem 0.25rem',
      borderRadius: '12px',
      background: isActive ? `${stage.color}15` : 'transparent',
      border: `1px solid ${isActive ? stage.color + '40' : 'transparent'}`,
      transition: 'all 0.25s',
    }}
    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
  >
    <div style={{
      width: 40, height: 40, borderRadius: '50%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: isDone ? 'rgba(16,185,129,0.15)' : isActive ? `${stage.color}20` : 'rgba(255,255,255,0.05)',
      border: `2px solid ${isDone ? '#10b981' : isActive ? stage.color : 'rgba(255,255,255,0.1)'}`,
      color: isDone ? '#10b981' : isActive ? stage.color : '#475569',
      transition: 'all 0.3s',
    }}>
      {isDone
        ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        : stage.icon
      }
    </div>
    <span style={{
      fontSize: '0.6rem', fontWeight: 700, textAlign: 'center', lineHeight: 1.3,
      color: isActive ? stage.color : isDone ? '#34d399' : '#475569',
      letterSpacing: '0.02em',
    }}>
      {stage.label}
    </span>
  </div>
);

// ── Main modal ────────────────────────────────────────────────
const HowItWorksModal = ({ onClose }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef(null);
  const stage = STAGES[activeIdx];

  useEffect(() => {
    if (autoPlay) {
      intervalRef.current = setInterval(() => {
        setActiveIdx(i => (i + 1) % STAGES.length);
      }, 4000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoPlay, activeIdx]);

  const handleStageClick = (i) => {
    setAutoPlay(false);
    clearInterval(intervalRef.current);
    setActiveIdx(i);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
    }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 680,
        background: '#0d1529',
        border: '1px solid #1e293b',
        borderRadius: '20px',
        overflow: 'hidden',
        maxHeight: '90vh',
        overflowY: 'auto',
      }}>

        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem 1rem',
          borderBottom: '1px solid #1e293b',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ color: '#f1f5f9', fontSize: '1.1rem', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
              How the agent works
            </h2>
            <p style={{ color: '#475569', fontSize: '0.74rem', margin: '0.2rem 0 0' }}>
              5-stage AI pipeline — click any stage to explore
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '1px solid #1e293b',
              color: '#64748b', borderRadius: '8px', width: 32, height: 32,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.1rem', flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Pipeline flow */}
        <div style={{ padding: '1.25rem 1rem 0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {STAGES.map((s, i) => (
              <React.Fragment key={s.id}>
                <StageNode
                  stage={s}
                  isActive={i === activeIdx}
                  isDone={i < activeIdx}
                  onClick={() => handleStageClick(i)}
                />
                {i < STAGES.length - 1 && (
                  <FlowLine
                    color={STAGES[i].color}
                    active={i < activeIdx}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: '0.75rem' }}>
            {STAGES.map((s, i) => (
              <div
                key={i}
                onClick={() => handleStageClick(i)}
                style={{
                  width: i === activeIdx ? 20 : 6,
                  height: 6, borderRadius: 3,
                  background: i === activeIdx ? stage.color : i < activeIdx ? '#10b981' : 'rgba(255,255,255,0.1)',
                  transition: 'all 0.3s', cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>

        {/* Active stage detail */}
        <div style={{ padding: '0 1.5rem 1.5rem' }}>
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            border: `1px solid ${stage.color}25`,
            borderLeft: `3px solid ${stage.color}`,
            borderRadius: '12px',
            padding: '1.1rem 1.25rem',
            marginBottom: '1rem',
            transition: 'border-color 0.3s',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem' }}>
              <div style={{ color: stage.color, display: 'flex' }}>{stage.icon}</div>
              <h3 style={{ color: '#e2e8f0', fontSize: '0.95rem', fontWeight: 700, margin: 0 }}>
                {stage.title}
              </h3>
            </div>
            <p style={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: 1.7, margin: 0 }}>
              {stage.detail}
            </p>
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem', marginBottom: '1rem' }}>
            {stage.stats.map((stat, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '10px', padding: '0.75rem',
                textAlign: 'center',
              }}>
                <div style={{ color: stage.color, fontSize: '1.3rem', fontWeight: 800, lineHeight: 1 }}>
                  {stat.value}
                </div>
                <div style={{ color: '#94a3b8', fontSize: '0.7rem', fontWeight: 600, marginTop: '0.25rem' }}>
                  {stat.label}
                </div>
                <div style={{ color: '#475569', fontSize: '0.65rem', marginTop: '0.1rem' }}>
                  {stat.sub}
                </div>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => handleStageClick(Math.max(0, activeIdx - 1))}
              disabled={activeIdx === 0}
              style={{
                padding: '0.5rem 1rem', borderRadius: '8px', cursor: activeIdx === 0 ? 'not-allowed' : 'pointer',
                background: 'rgba(255,255,255,0.04)', border: '1px solid #1e293b',
                color: activeIdx === 0 ? '#334155' : '#94a3b8', fontSize: '0.78rem', fontWeight: 600,
              }}
            >
              ← Previous
            </button>

            <button
              onClick={() => setAutoPlay(p => !p)}
              style={{
                padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer',
                background: autoPlay ? `${stage.color}15` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${autoPlay ? stage.color + '40' : '#1e293b'}`,
                color: autoPlay ? stage.color : '#64748b', fontSize: '0.72rem', fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: '0.35rem',
              }}
            >
              {autoPlay ? (
                <>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><rect x="1" y="1" width="3" height="8"/><rect x="6" y="1" width="3" height="8"/></svg>
                  Pause
                </>
              ) : (
                <>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M2 1l7 4-7 4z"/></svg>
                  Auto-play
                </>
              )}
            </button>

            <button
              onClick={() => handleStageClick(Math.min(STAGES.length - 1, activeIdx + 1))}
              disabled={activeIdx === STAGES.length - 1}
              style={{
                padding: '0.5rem 1rem', borderRadius: '8px', cursor: activeIdx === STAGES.length - 1 ? 'not-allowed' : 'pointer',
                background: 'rgba(255,255,255,0.04)', border: '1px solid #1e293b',
                color: activeIdx === STAGES.length - 1 ? '#334155' : '#94a3b8', fontSize: '0.78rem', fontWeight: 600,
              }}
            >
              Next →
            </button>
          </div>
        </div>

        {/* Footer note */}
        <div style={{
          padding: '0.75rem 1.5rem',
          borderTop: '1px solid #0f172a',
          background: 'rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="6" cy="6" r="5" stroke="#334155" strokeWidth="1"/>
            <path d="M6 5v4M6 3.5v.5" stroke="#334155" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          <p style={{ color: '#334155', fontSize: '0.68rem', margin: 0, lineHeight: 1.5 }}>
            Rankings are AI-generated editorial research for educational purposes only. Not investment advice.
            InvestorQ is not a registered investment advisor.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksModal;